import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFacilityRulesApi, updateFacilityRulesApi, getCourtsApi, createCourtApi, updateCourtApi, getStaffApi, registerStaffApi, changePasswordApi } from '../lib/api';
import { ArrowLeft, Save, Plus, Shield, Lock, Upload, QrCode } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function OperatorSettings() {
  const navigate = useNavigate();
  const { user, loading: authLoading, hasPermission } = useAuth();

  const [rules, setRules] = useState({});
  const [courts, setCourts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [newCourtName, setNewCourtName] = useState('');
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffPassword, setNewStaffPassword] = useState('');
  const [newStaffRole, setNewStaffRole] = useState('front_desk');

  // Change password state
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // GCash config state
  const [gcashName, setGcashName] = useState('');
  const [gcashNumber, setGcashNumber] = useState('');
  const [gcashQrImage, setGcashQrImage] = useState('');
  const [gcashSaving, setGcashSaving] = useState(false);
  const [qrUploading, setQrUploading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    try {
      const [rulesRes, courtsRes, staffRes] = await Promise.all([
        getFacilityRulesApi(),
        getCourtsApi(),
        getStaffApi(),
      ]);
      setRules(rulesRes.data);
      setCourts(courtsRes.data);
      setStaff(staffRes.data);
      // Populate GCash fields
      setGcashName(rulesRes.data.gcash_account_name || '');
      setGcashNumber(rulesRes.data.gcash_number || '');
      setGcashQrImage(rulesRes.data.gcash_qr_image || '');
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const handleSaveRules = async () => {
    try {
      await updateFacilityRulesApi(rules);
      toast.success('Rules saved');
    } catch (err) {
      toast.error('Failed to save rules');
    }
  };

  const handleAddCourt = async () => {
    if (!newCourtName.trim()) return;
    try {
      await createCourtApi({ name: newCourtName.trim(), features: [], is_active: true });
      setNewCourtName('');
      loadData();
      toast.success('Court added');
    } catch (err) {
      toast.error('Failed to add court');
    }
  };

  const handleAddStaff = async () => {
    if (!newStaffName || !newStaffEmail || !newStaffPassword) {
      toast.error('Fill in all fields');
      return;
    }
    try {
      await registerStaffApi({
        name: newStaffName.trim(),
        email: newStaffEmail.trim(),
        password: newStaffPassword,
        role: newStaffRole,
      });
      setNewStaffName('');
      setNewStaffEmail('');
      setNewStaffPassword('');
      loadData();
      toast.success('Staff member added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add staff');
    }
  };

  const handleChangePassword = async () => {
    if (!currentPw || !newPw) { toast.error('Fill in all fields'); return; }
    if (newPw.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    setPwLoading(true);
    try {
      await changePasswordApi(currentPw, newPw);
      toast.success('Password updated');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const handleSaveGcash = async () => {
    setGcashSaving(true);
    try {
      await updateFacilityRulesApi({ gcash_account_name: gcashName, gcash_number: gcashNumber });
      toast.success('GCash details saved');
    } catch (err) {
      toast.error('Failed to save GCash details');
    } finally {
      setGcashSaving(false);
    }
  };

  const handleQrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setQrUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('pp_token');
      const res = await fetch(`${API_URL}/api/facility-rules/gcash-qr`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setGcashQrImage(data.gcash_qr_image);
      toast.success('QR code uploaded');
    } catch (err) {
      toast.error('Failed to upload QR code');
    } finally {
      setQrUploading(false);
    }
  };



  const roleColors = {
    admin: 'bg-brand-teal-tint text-brand-teal',
    manager: 'bg-coaching-tint text-coaching',
    front_desk: 'bg-open-play-tint text-open-play',
    marshal: 'bg-status-held-bg text-status-held',
    viewer: 'bg-blocks-tint text-blocks',
  };

  if (authLoading || !user) return null;

  return (
    <div className="min-h-screen bg-app-bg" data-testid="operator-settings">
      {/* Header */}
      <div className="bg-white border-b border-black/5 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button
            data-testid="settings-back-btn"
            onClick={() => navigate('/operator')}
            className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="font-heading text-xl font-bold text-gray-900">Settings</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <Tabs defaultValue="rules" className="space-y-6">
          <TabsList className="bg-white border border-black/5 rounded-xl p-1" data-testid="settings-tabs">
            <TabsTrigger value="rules" className="rounded-lg text-sm data-[state=active]:bg-brand-teal data-[state=active]:text-white">Rules</TabsTrigger>
            <TabsTrigger value="courts" className="rounded-lg text-sm data-[state=active]:bg-brand-teal data-[state=active]:text-white">Courts</TabsTrigger>
            {hasPermission('manage_staff') && (
              <TabsTrigger value="staff" className="rounded-lg text-sm data-[state=active]:bg-brand-teal data-[state=active]:text-white">Staff</TabsTrigger>
            )}
            <TabsTrigger value="account" className="rounded-lg text-sm data-[state=active]:bg-brand-teal data-[state=active]:text-white">Account</TabsTrigger>
            <TabsTrigger value="gcash" className="rounded-lg text-sm data-[state=active]:bg-brand-teal data-[state=active]:text-white">GCash</TabsTrigger>
          </TabsList>

          {/* Rules Tab */}
          <TabsContent value="rules">
            <div className="bg-white rounded-3xl border border-black/5 p-6 space-y-6" data-testid="rules-form">
              <h2 className="font-heading text-lg font-semibold text-gray-900">Facility Rules</h2>

              {/* Court Pricing (Time-of-day) */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-xs uppercase tracking-widest font-semibold text-brand-teal">Court Pricing</h3>
                  <p className="text-xs text-gray-500 mt-1">Bookings spanning the evening cutoff are billed hybrid (per-hour).</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Day Rate (₱/hr)</Label>
                    <Input
                      data-testid="rules-day-rate"
                      type="number"
                      value={rules.day_rate ?? ''}
                      onChange={(e) => setRules({ ...rules, day_rate: parseFloat(e.target.value) })}
                      className="rounded-xl mt-1"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">Applies before evening cutoff</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Evening Rate (₱/hr)</Label>
                    <Input
                      data-testid="rules-evening-rate"
                      type="number"
                      value={rules.evening_rate ?? ''}
                      onChange={(e) => setRules({ ...rules, evening_rate: parseFloat(e.target.value) })}
                      className="rounded-xl mt-1"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">Applies from cutoff onwards</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Evening Cutoff Hour (24h)</Label>
                    <Input
                      data-testid="rules-evening-start-hour"
                      type="number"
                      min="0"
                      max="23"
                      value={rules.evening_start_hour ?? ''}
                      onChange={(e) => setRules({ ...rules, evening_start_hour: parseInt(e.target.value) })}
                      className="rounded-xl mt-1"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">16 = 4 PM</p>
                  </div>
                </div>
              </div>

              {/* Equipment Rental */}
              <div className="space-y-3 pt-2 border-t border-divider">
                <div>
                  <h3 className="text-xs uppercase tracking-widest font-semibold text-brand-teal">Equipment Rental (Drills)</h3>
                  <p className="text-xs text-gray-500 mt-1">Per-hour add-on. Operator side gets a discounted rate.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Player Rate (₱/hr)</Label>
                    <Input
                      data-testid="rules-equip-player"
                      type="number"
                      value={rules.equipment_rate_player ?? ''}
                      onChange={(e) => setRules({ ...rules, equipment_rate_player: parseFloat(e.target.value) })}
                      className="rounded-xl mt-1"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">Self-service booking</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Operator Rate (₱/hr)</Label>
                    <Input
                      data-testid="rules-equip-operator"
                      type="number"
                      value={rules.equipment_rate_operator ?? ''}
                      onChange={(e) => setRules({ ...rules, equipment_rate_operator: parseFloat(e.target.value) })}
                      className="rounded-xl mt-1"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">When operator creates the booking</p>
                  </div>
                </div>
              </div>

              {/* Other rules */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-divider">
                <div>
                  <Label className="text-sm text-gray-600">Buffer Between Bookings (min)</Label>
                  <Input
                    data-testid="rules-buffer"
                    type="number"
                    value={rules.buffer_minutes || ''}
                    onChange={(e) => setRules({ ...rules, buffer_minutes: parseInt(e.target.value) })}
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Max Booking Hours</Label>
                  <Input
                    data-testid="rules-max-hours"
                    type="number"
                    value={rules.max_booking_hours || ''}
                    onChange={(e) => setRules({ ...rules, max_booking_hours: parseInt(e.target.value) })}
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Auto-expire Unpaid (min)</Label>
                  <Input
                    data-testid="rules-auto-expire"
                    type="number"
                    value={rules.auto_expire_minutes || ''}
                    onChange={(e) => setRules({ ...rules, auto_expire_minutes: parseInt(e.target.value) })}
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Operating Start Hour</Label>
                  <Input
                    data-testid="rules-start-hour"
                    type="number"
                    value={rules.operating_start_hour || ''}
                    onChange={(e) => setRules({ ...rules, operating_start_hour: parseInt(e.target.value) })}
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Operating End Hour</Label>
                  <Input
                    data-testid="rules-end-hour"
                    type="number"
                    value={rules.operating_end_hour || ''}
                    onChange={(e) => setRules({ ...rules, operating_end_hour: parseInt(e.target.value) })}
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <div>
                  <Label className="text-sm text-gray-600">Open Play Overrides Rentals</Label>
                  <p className="text-xs text-gray-400 mt-0.5">When enabled, open play blocks take priority</p>
                </div>
                <Switch
                  data-testid="rules-open-play-override"
                  checked={rules.open_play_override || false}
                  onCheckedChange={(val) => setRules({ ...rules, open_play_override: val })}
                />
              </div>
              <Button
                data-testid="save-rules-btn"
                onClick={handleSaveRules}
                disabled={!hasPermission('edit_settings')}
                className="bg-brand-teal hover:bg-brand-teal-hover text-white rounded-full px-8"
              >
                <Save className="w-4 h-4 mr-2" /> Save Rules
              </Button>
            </div>
          </TabsContent>

          {/* Courts Tab */}
          <TabsContent value="courts">
            <div className="bg-white rounded-3xl border border-black/5 p-6 space-y-4" data-testid="courts-management">
              <h2 className="font-heading text-lg font-semibold text-gray-900">Courts</h2>
              {courts.map(court => (
                <div key={court.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                  <div>
                    <span className="text-sm font-medium text-gray-800">{court.name}</span>
                    <div className="flex gap-1 mt-1">
                      {(court.features || []).map(f => (
                        <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>
                      ))}
                    </div>
                  </div>
                  <Badge className={court.is_active ? 'bg-open-play-tint text-open-play' : 'bg-gray-100 text-gray-500'}>
                    {court.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              ))}
              {hasPermission('edit_settings') && (
                <div className="flex gap-2">
                  <Input
                    data-testid="new-court-name"
                    value={newCourtName}
                    onChange={(e) => setNewCourtName(e.target.value)}
                    placeholder="Court name"
                    className="rounded-xl"
                  />
                  <Button
                    data-testid="add-court-btn"
                    onClick={handleAddCourt}
                    className="bg-brand-teal hover:bg-brand-teal-hover text-white rounded-xl px-6"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Staff Tab */}
          {hasPermission('manage_staff') && (
            <TabsContent value="staff">
              <div className="bg-white rounded-3xl border border-black/5 p-6 space-y-4" data-testid="staff-management">
                <h2 className="font-heading text-lg font-semibold text-gray-900">Staff Members</h2>
                {staff.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                    <div>
                      <span className="text-sm font-medium text-gray-800">{s.name}</span>
                      <p className="text-xs text-gray-400">{s.email}</p>
                    </div>
                    <Badge className={roleColors[s.role] || 'bg-gray-100 text-gray-600'}>
                      <Shield className="w-3 h-3 mr-1" /> {s.role}
                    </Badge>
                  </div>
                ))}

                <div className="border-t border-gray-100 pt-4 space-y-3">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Add Staff</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input data-testid="new-staff-name" value={newStaffName} onChange={(e) => setNewStaffName(e.target.value)} placeholder="Name" className="rounded-xl" />
                    <Input data-testid="new-staff-email" value={newStaffEmail} onChange={(e) => setNewStaffEmail(e.target.value)} placeholder="Email" className="rounded-xl" />
                    <Input data-testid="new-staff-password" value={newStaffPassword} onChange={(e) => setNewStaffPassword(e.target.value)} placeholder="Password" type="password" className="rounded-xl" />
                    <Select value={newStaffRole} onValueChange={setNewStaffRole}>
                      <SelectTrigger className="rounded-xl" data-testid="new-staff-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="front_desk">Front Desk</SelectItem>
                        <SelectItem value="marshal">Marshal / Coach</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    data-testid="add-staff-btn"
                    onClick={handleAddStaff}
                    className="bg-brand-teal hover:bg-brand-teal-hover text-white rounded-full px-8"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Staff
                  </Button>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Account Tab */}
          <TabsContent value="account">
            <div className="bg-white rounded-3xl border border-black/5 p-6 space-y-4" data-testid="change-password-section">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-gray-500" />
                <h2 className="font-heading text-lg font-semibold text-gray-900">Change Password</h2>
              </div>
              <div className="space-y-3 max-w-sm">
                <div>
                  <Label className="text-sm text-gray-600 mb-1.5">Current Password</Label>
                  <Input
                    type="password"
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    placeholder="Enter current password"
                    className="rounded-xl"
                    data-testid="current-password-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600 mb-1.5">New Password</Label>
                  <Input
                    type="password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="Enter new password"
                    className="rounded-xl"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600 mb-1.5">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="Confirm new password"
                    className="rounded-xl"
                    data-testid="confirm-new-password-input"
                  />
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={pwLoading}
                  className="bg-gray-900 hover:bg-gray-800 text-white rounded-full px-8"
                  data-testid="change-password-btn"
                >
                  {pwLoading ? 'Updating...' : 'Update Password'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* GCash Tab */}
          <TabsContent value="gcash">
            <div className="bg-white rounded-3xl border border-black/5 p-6 space-y-5" data-testid="gcash-config-section">
              <div className="flex items-center gap-2 mb-2">
                <QrCode className="w-4 h-4 text-brand-teal" />
                <h2 className="font-heading text-lg font-semibold text-gray-900">GCash Payment Details</h2>
              </div>
              <p className="text-xs text-gray-400">These details are shown to players when they need to send payment.</p>

              <div className="space-y-3 max-w-sm">
                <div>
                  <Label className="text-sm text-gray-600 mb-1.5">Account Name</Label>
                  <Input
                    value={gcashName}
                    onChange={(e) => setGcashName(e.target.value)}
                    placeholder="e.g. Saul Lerado"
                    className="rounded-xl"
                    data-testid="gcash-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600 mb-1.5">GCash Number</Label>
                  <Input
                    value={gcashNumber}
                    onChange={(e) => setGcashNumber(e.target.value)}
                    placeholder="e.g. 0936 064 6939"
                    className="rounded-xl"
                    data-testid="gcash-number-input"
                  />
                </div>
                <Button
                  onClick={handleSaveGcash}
                  disabled={gcashSaving}
                  className="bg-gray-900 hover:bg-gray-800 text-white rounded-full px-8"
                  data-testid="save-gcash-btn"
                >
                  {gcashSaving ? 'Saving...' : 'Save Details'}
                </Button>
              </div>

              {/* QR Code */}
              <div className="border-t pt-5 mt-5">
                <Label className="text-sm text-gray-600 mb-3 block">QR Code</Label>
                {gcashQrImage && (
                  <div className="mb-4 max-w-[240px]">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${gcashQrImage}`}
                      alt="GCash QR"
                      className="rounded-xl border border-gray-100 w-full"
                      data-testid="gcash-qr-preview"
                    />
                  </div>
                )}
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors text-sm text-gray-600">
                  <Upload className="w-4 h-4" />
                  {qrUploading ? 'Uploading...' : gcashQrImage ? 'Replace QR' : 'Upload QR'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleQrUpload}
                    className="hidden"
                    disabled={qrUploading}
                    data-testid="gcash-qr-upload-input"
                  />
                </label>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
